import React from 'react';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopNavBar from '../user/top_nav_bar';
import ProgrammeProgressMealCard from './programme_progress_meal_card';
import SharedProgrammeWithUserEnrolledPopupMenu from './shared_programme_with_user_enrolled_popup_menu';
import {
  daysSince,
  mergeMyDayActionRecords,
  sharedProgrammeDurationDays,
} from '../../services/programmes';
import ProgrammeProgressContentEntryCard from './programme_progress_content_entry_card';
import { END_USER_SHARED_PROGRAMMES_ROUTE } from '../../services/routes';
import {
  useDeleteContentEntryMutation,
  useDeleteMealMutation,
  useDeleteNoteMutation,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useGetSharedProgrammeQuery,
  useUpdateDbPlannerWeekMutation,
  useUpdateUserProgrammeEnrollmentMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  isProgrammeEnrollmentCompleted,
  programmeStartCalendarDay,
  programmeTotalDays,
  timelineItemNodesFor,
} from '../../services/my_day';
import { calendarDayFromJSDate } from '../../services/dates';
import { trackEvents } from '../../operations/tracking_operations';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { clearPlanner } from '../../operations/planner_operations';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const ProgrammeProgress = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(currentProgrammeEnrollment?.id, {
      skip: !currentProgrammeEnrollment?.id,
    });

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(currentSpaceMembership?.id, {
      skip: !currentSpaceMembership?.id,
    });

  const { sharedProgrammeID } = currentProgrammeEnrollment || {};

  const programmeStartDate = programmeStartCalendarDay(
    currentProgrammeEnrollment,
  );

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const [updateUserProgrammeEnrollment] =
    useUpdateUserProgrammeEnrollmentMutation();

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    sharedProgrammeID,
    {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const sharedProgrammeTitle = sharedProgramme?.title;

  const programmeLength = sharedProgramme
    ? programmeTotalDays(sharedProgramme)
    : 0;

  const enrollmentStartDate =
    currentProgrammeEnrollment &&
    programmeStartCalendarDay(currentProgrammeEnrollment);

  const enrollmentEffectiveEndDate =
    currentProgrammeEnrollment &&
    dayjs(enrollmentStartDate).add(programmeLength, 'day').format('YYYY-MM-DD');

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [searchParams] = useSearchParams();

  const todayOverride = searchParams.get('asOf');

  const today = todayOverride || calendarDayFromJSDate(new Date());

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const timelineItems = timelineItemNodesFor(
    currentProgrammeEnrollment,
    sharedProgramme,
    myDayActionRecords,
    today,
  );
  console.log(JSON.stringify(timelineItems));

  // const clearPlannerDay = async (plannerViewWeekStartDate, dayIndex) => {
  //   const plannerDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
  //     plannerViewWeekStartDate,
  //     dayIndex,
  //   );

  //   const plannerWeek = dbPlannerWeeksByStartDate[plannerDbWeekStartDate];

  //   const plannerDay = plannerWeek.days[daysArrayIndex(dayIndex)];
  //   if (!plannerDay) {
  //     console.warn(
  //       `Cannot find planner day for DB week start date ${plannerDbWeekStartDate} day index ${dayIndex}`,
  //     );
  //     return null;
  //   }
  //   const mealIDs = plannerDay.entries
  //     .filter(
  //       (e) =>
  //         !e.plannerEntryType || e.plannerEntryType === PlannerEntryType.MEAL,
  //     )
  //     .map((e) => e.mealID);
  //   const noteIDs = plannerDay.entries
  //     .filter((e) => e.plannerEntryType === PlannerEntryType.NOTE)
  //     .map((e) => e.objectID);
  //   const contentEntryIDs = plannerDay.entries
  //     .filter((e) => e.plannerEntryType === PlannerEntryType.CONTENT_ENTRY)
  //     .map((e) => e.objectID);
  //   const removePromises = [];
  //   if (mealIDs.length > 0) {
  //     dispatch(mealsRemovedFromMealBasket(mealIDs));
  //     removePromises.push(
  //       ...mealIDs.map((mealID) =>
  //         deleteMeal({ id: mealID, parentID: plannerWeek.id }),
  //       ),
  //     );
  //   }
  //   if (noteIDs.length > 0) {
  //     removePromises.push(
  //       ...noteIDs.map((noteID) =>
  //         deleteNote({ id: noteID, parentID: plannerWeek.id }),
  //       ),
  //     );
  //   }
  //   if (contentEntryIDs.length > 0) {
  //     removePromises.push(
  //       ...contentEntryIDs.map((contentEntryID) =>
  //         deleteContentEntry({ id: contentEntryID, parentID: plannerWeek.id }),
  //       ),
  //     );
  //   }
  //   if (removePromises.length > 0) {
  //     try {
  //       await Promise.allSettled(removePromises);
  //       // TODO use result
  //     } catch (e) {
  //       // Swallow any errors
  //       console.warn(e);
  //     }
  //   }
  //   const updatedDbPlannerWeek = {
  //     ...plannerWeek,
  //   };
  //   updatedDbPlannerWeek.days[daysArrayIndex(dayIndex)] = {
  //     ...updatedDbPlannerWeek.days[daysArrayIndex(dayIndex)],
  //     entries: [],
  //   };
  //   return updateDbPlannerWeek(updatedDbPlannerWeek);
  // };

  // const clearPlanner = async (fromCalendarDay, toCalendarDay) => {
  //   for (
  //     let currentCalendarDay = fromCalendarDay;
  //     dayjs(currentCalendarDay).isBefore(dayjs(toCalendarDay));
  //     currentCalendarDay = dayjs(currentCalendarDay)
  //       .add(1, 'day')
  //       .format('YYYY-MM-DD')
  //   ) {
  //     console.log(currentCalendarDay);
  //     const plannerViewWeekStartDate = viewWeekStartDateStr(
  //       currentCalendarDay,
  //       1,
  //     );
  //     const dayIndex = dayIndexForCalendarDate(currentCalendarDay);
  //     console.log(
  //       `Deleting objects, plannerViewWeekStartDate ${plannerViewWeekStartDate}, dayIndex ${dayIndex}`,
  //     );
  //     // eslint-disable-next-line no-await-in-loop
  //     await clearPlannerDay(plannerViewWeekStartDate, dayIndex);
  //   }
  // };

  /** TODO duplicate code in programme_progress_not_yet_started */
  const onStopProgram = async (shouldClearPlanner) => {
    console.log('Before navigate');
    navigate(END_USER_SHARED_PROGRAMMES_ROUTE);
    console.log(
      'Before endProgrammeEnrollmentAction, shouldClearPlanner:',
      shouldClearPlanner,
    );
    const programmeEnrollmentEnding = {
      ...currentProgrammeEnrollment,
      endedAt: new Date().toISOString(),
    };
    await updateUserProgrammeEnrollment(programmeEnrollmentEnding);
    if (shouldClearPlanner) {
      const clearFromDate = today;
      console.log(
        `Clearing planner from ${clearFromDate} to ${enrollmentEffectiveEndDate}`,
      );
      clearPlanner(
        clearFromDate,
        enrollmentEffectiveEndDate,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        dispatch,
        deleteMeal,
        deleteNote,
        deleteContentEntry,
        updateDbPlannerWeek,
      );
    }
    console.log('Before showGlobalSnackbar');
    dispatch(
      showGlobalSnackbar({
        notificationText: 'You have stopped the program',
      }),
    );
  };

  const daysSinceStart = daysSince(today, programmeStartDate) + 1;

  console.log({ daysSinceStart, programmeLength });

  const displayedDaysSinceStart =
    daysSinceStart > programmeLength ? programmeLength : daysSinceStart;

  const onStartProgram = () => {
    trackEvents([
      {
        name: 'Program started',
        args: {
          program_name: sharedProgramme.title,
          space_id: sharedProgramme.spaceId,
          program_id: sharedProgramme.id,
        },
      },
    ]);
    navigate(`/shared_programmes/${sharedProgramme.id}/onboard`);
  };

  const isCompleted =
    currentProgrammeEnrollment &&
    isProgrammeEnrollmentCompleted(
      currentProgrammeEnrollment,
      sharedProgrammeDurationDays(sharedProgramme),
      today,
    );

  if (!currentProgrammeEnrollment) {
    return null;
  }

  return (
    <div className="shared-programmes-view">
      <TopNavBar />
      <div className="topNavBarMobile">
        <div style={{ paddingLeft: '23px', margin: 'auto 0' }}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            href="#"
            style={{ textDecoration: 'none' }}
            onClick={(ev) => {
              ev.preventDefault();
              navigate(-1);
            }}>
            <FontAwesomeIcon icon="chevron-left" />
            Back
          </a>
        </div>
        <div style={{ margin: 'auto', marginRight: 10 }}>
          <SharedProgrammeWithUserEnrolledPopupMenu
            isCompleted={isCompleted}
            onStopProgram={onStopProgram}
            onStartProgram={onStartProgram}
          />
        </div>
      </div>
      <h2
        style={{
          textAlign: 'left',
          fontSize: '24px',
          color: 'var(--app-primary-font-color)',
          margin: '23px 0 10px 23px',
        }}>
        {sharedProgrammeTitle}
      </h2>
      <h3
        style={{
          textAlign: 'left',
          fontSize: '16px',
          color: 'var(--app-primary-font-color)',
          margin: '10px 0 10px 23px',
        }}>
        Day {displayedDaysSinceStart} of {programmeLength}
      </h3>
      <div className="shared-programmes-view">
        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}>
          {timelineItems.map((node, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot />
                {index !== timelineItems.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                {node.nodeLabel}
                {(node.items || []).map((item) => {
                  if (item.itemType === 'CONTENT_ENTRY') {
                    return (
                      <ProgrammeProgressContentEntryCard
                        key={item.itemID}
                        contentEntryID={item.itemID}
                        parentID={item.parentID}
                      />
                    );
                  }
                  if (item.itemType === 'MEAL') {
                    return (
                      <ProgrammeProgressMealCard
                        key={item.itemID}
                        mealID={item.itemID}
                        parentID={item.parentID}
                      />
                    );
                  }
                  return null;
                })}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </div>
    </div>
  );
};

export default ProgrammeProgress;
